import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { PROOF_STRIP, MILESTONE_RIBBON, VISITOR_MAPS } from '../../../content/landingHome';
import StatBand, { STAT_ITEM, STAT_LINK, STAT_NUMERAL, STAT_TINT } from '../../StatBand';
import VisitorMaps from './VisitorMaps';
import MilestoneBadge from './MilestoneBadge';
import { EVENTS, track } from '../../../services/analytics';
import useTopShortsFlag from '../../../hooks/useTopShortsFlag';

// The served /videos link lands on the most-watched list while
// videos_top_shorts is on (#5433, founder answer 5). The served href itself
// (landing_stats.VIDEOS_HREF) and the ribbon's words never change.
export const withTopAnchor = (href, on) => (on && href === '/videos' ? '/videos#most-watched' : href);

// The landing proof strip and milestone ribbon (#3820; founder 2026-10-11:
// "lets wait for the threshold to make the landing page final and from then
// on the stats should reflect the most current stat").
//
// Both render ONLY what get-landing-stats returns with `live: true`, which
// the server sends after the founder's Go live on the Desk and only for
// figures that cleared their thresholds (workflow_jobs/landing_stats.py). A
// failed read, `live: false` or an empty list renders nothing: never a small
// number, never a placeholder.

const ROUTE = /^\/(?!\/)/;
// A badge is a figure (the server's own check, landing_stats_public._BADGE).
const BADGE = /^[0-9][0-9,]{0,12}[+%]?$/;

/** Mock variant A: four figures under Ask Astra, 2x2 on a phone, one row on
 * desktop, on the shared green band since #4598 (founder 2026-10-17). */
export function ProofStrip({ stats }) {
    const served = stats?.live === true && Array.isArray(stats.tiles) ? stats.tiles.filter((t) => t?.value && t?.label) : [];
    if (!served.length) return null;
    // Where visitors come from (#4607, mock D): the reach block's figures join
    // the band as two more tiles, then the line and the maps under them.
    const reach = stats.reach && typeof stats.reach === 'object' && stats.reach.states && stats.reach.us ? stats.reach : null;
    const tiles = reach ? [...served,
        { key: 'states_reached', value: reach.states, label: VISITOR_MAPS.statesLabel },
        ...(reach.countries && reach.world ? [{ key: 'countries_reached', value: reach.countries, label: VISITOR_MAPS.countriesLabel }] : []),
    ] : served;
    const wide = tiles.length > 4;
    return (
        <StatBand data-landing-section="proof_strip" data-testid="landing-proof-strip" aria-label={PROOF_STRIP.label}
            className="px-4 py-8 sm:px-6 lg:px-8 lg:py-10">
            <div className={`mx-auto ${wide ? 'max-w-6xl' : 'max-w-4xl'}`}>
                <ul className={`m-0 grid list-none grid-cols-2 gap-x-4 gap-y-5 p-0 sm:gap-x-6 ${wide ? 'sm:grid-cols-3 lg:grid-cols-6' : 'sm:grid-cols-4'}`}>
                    {tiles.map((t) => {
                        const linked = typeof t.href === 'string' && ROUTE.test(t.href) && !t.href.includes('#');
                        // A phone has no hover: a linked figure's label is underlined, as
                        // the band's source links are, so it reads as a link at rest.
                        const body = (
                            <>
                                <span className={`block ${STAT_NUMERAL} text-3xl sm:text-4xl`}>{t.value}</span>
                                <span className={`mt-2 block text-sm leading-snug ${STAT_TINT} [text-wrap:pretty] ${linked ? STAT_LINK : ''}`}>{t.label}</span>
                            </>
                        );
                        return (
                            <li key={t.key} className={STAT_ITEM}>
                                {linked ? (
                                    <Link to={t.href} data-cta="proof_strip_link"
                                        className="-mx-2 block min-h-[44px] rounded-lg px-2 py-1 hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-white">
                                        {body}
                                    </Link>
                                ) : (
                                    <div className="py-1">{body}</div>
                                )}
                            </li>
                        );
                    })}
                </ul>
                <VisitorMaps reach={reach} />
                <p className={`mt-5 text-center text-sm ${STAT_TINT}`}>{PROOF_STRIP.footnote}</p>
            </div>
        </StatBand>
    );
}

const DISMISSED = 'stratia_ribbon_dismissed';

function wasDismissed(id) {
    try {
        return window.localStorage.getItem(DISMISSED) === id;
    } catch {
        return false;
    }
}

/** Mock variant C: one line at the top for the 14 days after he approves a milestone. */
export function MilestoneRibbon({ stats }) {
    const ribbon = stats?.live === true ? stats.ribbon : null;
    const topOn = useTopShortsFlag();
    const [hidden, setHidden] = useState(() => Boolean(ribbon?.id) && wasDismissed(ribbon.id));
    if (!ribbon?.id || !ribbon.text || hidden || wasDismissed(ribbon.id)) return null;
    const dismiss = () => {
        try {
            window.localStorage.setItem(DISMISSED, ribbon.id);
        } catch {
            // a private window: it stays dismissed for this page view only
        }
        setHidden(true);
    };
    const href = typeof ribbon.href === 'string' && ROUTE.test(ribbon.href) ? withTopAnchor(ribbon.href, topOn) : null;
    // The badge (#4681; founder 2026-10-18: "add a nice badge to it... make
    // it visible") is the milestone's own number, served from the milestone
    // table, never a live reading. Only a figure renders. Mock round #4681,
    // variant C (the milestone band): the badge with its label, the line, the
    // link as a button; phone gutters and the badge are tighter below `sm` so
    // the line holds at two lines on a 375px phone (measured 61px tall) with
    // the 44px dismiss target kept. Since #4719 the badge is an award mark,
    // a garnet ribbon wrapping the bar's edge (MilestoneBadge.jsx): founder
    // 2026-10-18 "more appealing in a different color like a ribbon".
    const badge = typeof ribbon.badge === 'string' && BADGE.test(ribbon.badge) ? ribbon.badge : null;
    // In the page's flow under the fixed header (64px): the wrapper clears
    // the header and gives the space back below (-mb-16), so the first
    // section's own top padding starts under the ribbon, whatever its height.
    return (
        <div className="relative z-40 -mb-16 pt-16">
            <div data-landing-section="milestone_ribbon" data-testid="landing-milestone-ribbon" role="note"
                className="bg-[#1A4D2E] text-white">
                <div className="mx-auto flex max-w-7xl items-center gap-2 px-3 py-2 sm:gap-3 sm:px-6 lg:px-8">
                    {badge && <MilestoneBadge badge={badge} label={MILESTONE_RIBBON.badgeLabel} />}
                    <p className="flex-1 text-sm leading-snug sm:text-base">
                        {ribbon.text}
                        {href && ribbon.link && (
                            <>
                                {' '}
                                {/* Founder "A1" (#4728): the views ribbons' "Watch them" opens /videos;
                                    that tap is its own event so the page's traffic from the ribbon reads
                                    directly. Every ribbon link keeps its data-cta. */}
                                <a href={href} data-cta="milestone_ribbon_link"
                                    onClick={() => { if (href.split('#')[0] === '/videos') track(EVENTS.RIBBON_WATCH_CLICKED, { milestone: ribbon.id }); }}
                                    className="ml-1 inline-block whitespace-nowrap rounded-full border border-white/50 px-2.5 py-0.5 text-sm font-semibold hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-white">
                                    {ribbon.link}
                                </a>
                            </>
                        )}
                    </p>
                    <button type="button" onClick={dismiss} data-cta="milestone_ribbon_dismiss"
                        aria-label={MILESTONE_RIBBON.dismiss}
                        className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-white">
                        <XMarkIcon className="h-5 w-5" aria-hidden="true" />
                    </button>
                </div>
            </div>
        </div>
    );
}
